import React, { useCallback, useEffect, useState } from 'react';
import { X, Cpu, Check, AlertCircle, Info } from 'lucide-react';
import { api } from '../../services/apiService';
import SearchableDropdown from '../SearchableDropdown';
import { RubyLoader } from '../RubyLoader';

interface Device {
  deviceId: string;
  deviceName: string;
  model: string;
  serialNumber: string;
  status: string;
  isActive: boolean;
  registerId: string | null;
  deviceClass?: string;
}

interface Props {
  tenantId: string;
  locationId: string;
  /** Current terminal's id — devices already assigned to this terminal are
   *  shown as the default pick; devices assigned to OTHER terminals are
   *  hidden (only unassigned + already-here are pickable). */
  terminalId: string;
  terminalName: string;
  /** Current device id already bound to this terminal, if any. */
  currentDeviceId?: string | null;
  onPicked: (deviceId: string, deviceClass: string) => void;    // parent calls the save
  onClose: () => void;
  onGoRegister: () => void;                 // nav to EMV Device tab for register
}

/**
 * Shows a SearchableDropdown of picky-able devices at this location and
 * surfaces a Register-POI call-to-action when the pool is empty. Parent
 * receives the chosen deviceId + deviceClass and is responsible for the PUT
 * /payment-types call (atomic assign + enabling the matching NMI credit method
 * — credit_nmi_cloud for cloud devices, credit_nmi_direct for VP3350).
 */
const AssignPoiModal: React.FC<Props> = ({
  tenantId, locationId, terminalId, terminalName, currentDeviceId, onPicked, onClose, onGoRegister,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [selected, setSelected] = useState<string | null>(currentDeviceId || null);

  const fetchPool = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.posNmi.listEmvDevicesAtLocation(tenantId, locationId);
      setDevices(res?.devices || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load device pool');
    } finally {
      setLoading(false);
    }
  }, [tenantId, locationId]);

  useEffect(() => { fetchPool(); }, [fetchPool]);

  // Pickable = active + (unassigned OR already bound to THIS terminal)
  const pickable = devices.filter(d => d.isActive && (!d.registerId || d.registerId === terminalId));
  const options = pickable.map(d => ({
    value: d.deviceId,
    label: d.deviceName,
    subtitle: `${d.model}${d.serialNumber ? ` · ${d.serialNumber}` : ''}${d.deviceClass === 'nmi_direct_vp3350' ? ' · VP3350' : ''}${d.deviceClass === 'dejavoo_spin_usb' ? ' · P17 USB' : ''}${d.registerId === terminalId ? ' · currently paired here' : ''}`,
  }));

  const handleConfirm = () => {
    if (!selected) return;
    const dev = devices.find(d => d.deviceId === selected);
    onPicked(selected, dev?.deviceClass || 'cloud');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
        <div className="flex items-center gap-3 p-5 border-b border-stone-100">
          <div className="w-10 h-10 rounded-lg bg-stone-900 flex items-center justify-center shrink-0">
            <Cpu size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-stone-900 font-semibold text-base">Assign POI device</h2>
            <p className="text-stone-500 text-xs mt-0.5 truncate">Pick one device from the location pool to bind to {terminalName}.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading && (
            <RubyLoader variant="section" />
          )}

          {!loading && error && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200">
              <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {!loading && !error && pickable.length === 0 && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-center">
              <Cpu size={20} className="mx-auto mb-2 text-amber-600" />
              <p className="text-sm font-medium text-amber-900">No POI devices available</p>
              <p className="text-xs text-amber-800 mt-1">
                Register a POI first. Devices can be reused across terminals — only one terminal can hold each device at a time.
              </p>
              <button
                onClick={onGoRegister}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Register POI
              </button>
            </div>
          )}

          {!loading && !error && pickable.length > 0 && (
            <>
              <div>
                <label className="block text-xs font-medium text-stone-700 mb-1">POI device</label>
                <SearchableDropdown
                  options={options}
                  value={selected}
                  onChange={setSelected}
                  placeholder="Choose a device from the pool"
                  searchPlaceholder="Search by name, model, serial…"
                />
              </div>
              <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 flex items-start gap-2">
                <Info size={12} className="text-stone-500 shrink-0 mt-0.5" />
                <div className="text-xs text-stone-600">
                  <p>Saving enables the matching NMI card method on this terminal and binds the device. Disabling it releases the device back to the pool.</p>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="p-4 border-t border-stone-100 bg-stone-50 rounded-b-2xl flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm font-medium text-stone-600 hover:text-stone-800">Cancel</button>
          <button
            onClick={handleConfirm}
            disabled={!selected || pickable.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Check size={14} />
            Assign &amp; enable
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignPoiModal;
